import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, ScrollText, Globe2, FileCheck2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LOGO = 'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

export default function Disclaimer() {
  useEffect(() => {
    document.title = 'Disclaimer — AlveeTrade';
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-[hsl(0_0%_4%)] text-[hsl(0_0%_98%)]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[hsla(0,0%,4%,0.85)] border-b border-border">
        <div className="container-x flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2 notranslate" translate="no">
            <img src={LOGO} alt="AlveeTrade" className="h-9 w-9 rounded-lg gold-hairline" />
            <span className="font-display uppercase text-xl tracking-wide">AlveeTrade</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/" data-testid="disclaimer-back-home">
              <Button variant="ghost" className="h-9 gap-2 text-white border border-[rgba(245,185,66,0.35)] hover:bg-[rgba(245,185,66,0.08)]">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero / Heading */}
      <section className="relative overflow-hidden hero-glow noise-overlay">
        <div className="container-x py-14 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[rgba(245,185,66,0.10)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.35)]">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)]">Official Disclaimer</span>
          </div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight max-w-3xl">
            READ BEFORE YOU JOIN.
          </h1>
          <p className="text-base md:text-lg text-[hsl(0_0%_82%)] mt-5 max-w-2xl leading-relaxed">
            By joining or remaining in any AlveeTrade channel, group, or page, you agree to the following terms and disclaimer in full.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[hsl(0_0%_4%)] section-pad">
        <div className="container-x">
          <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
            {/* Main content */}
            <article className="space-y-7" data-testid="disclaimer-body">
              <Block icon={ScrollText} title="Educational Purpose Only">
                This channel and all of its associated content, media, messages, signals, breakdowns, screenshots,
                videos, and materials are strictly for <Em>educational and informational purposes only</Em>.
                Nothing shared here — including text, images, videos, signals, ideas, or opinions — should ever be
                interpreted as financial advice, investment recommendations, or solicitation to buy or sell any
                asset, security, derivative, currency, or financial product.
              </Block>

              <Block icon={FileCheck2} title="No Investment Management">
                AlveeTrade does not accept investments from anyone, nor do we manage, hold, or trade funds on
                behalf of any individual or organization. Similarly, we do not provide financial, legal, or tax
                advice in any form. Every member is solely responsible for conducting their own research and
                making independent decisions before engaging in any kind of trading or investment activity.
              </Block>

              <Block icon={AlertTriangle} title="Risk Warning">
                Trading and investing involve <Em>substantial risk, including the potential loss of all invested capital</Em>.
                Past performance, screenshots of profits, hypothetical results, or any personal claims of
                profitability do not guarantee future returns. You are fully responsible for your financial
                decisions and for any resulting profits or losses.
              </Block>

              <Block icon={Globe2} title="Jurisdiction">
                AlveeTrade is operated by an independent education team. All content and activities within these
                channels are intended for general informational purposes. Use of the information, content, or
                strategies shared here in jurisdictions where such activity is restricted, regulated, or
                prohibited is strictly unauthorized, and AlveeTrade bears no responsibility or liability for any
                losses, damages, or legal issues arising from such unauthorized use. It is your responsibility to
                comply with the laws of your country.
              </Block>

              <Block icon={ShieldAlert} title="Your Acknowledgement">
                <p className="mb-3">By continuing to access, read, or engage with any AlveeTrade Telegram channel, group, page, or website, you explicitly confirm that you:</p>
                <ul className="space-y-2.5 mt-2">
                  <Li>Understand and accept all terms stated in this disclaimer.</Li>
                  <Li>Acknowledge that you are participating voluntarily and at your own risk.</Li>
                  <Li>
                    Release AlveeTrade, its operators, mentors, partners, and associated entities from any form
                    of legal, financial, or personal liability related to your trading or investment actions.
                  </Li>
                  <Li>Will not redistribute, copy, or resell any content from inside the community without written permission.</Li>
                </ul>
              </Block>

              <div className="rounded-2xl border border-[rgba(225,29,46,0.45)] bg-[rgba(225,29,46,0.06)] p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[hsl(0_84%_62%)] shrink-0 mt-0.5" />
                  <p className="text-[15px] leading-relaxed text-[hsl(0_0%_92%)]">
                    <Em className="text-[hsl(0_84%_62%)]">If you do not agree with any part of this disclaimer,
                    you must leave the channel immediately</Em> and discontinue all use of AlveeTrade content.
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground pt-4">Last updated: {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </article>

            {/* Sticky table of contents */}
            <aside className="hidden lg:block">
              <div className="sticky top-20 card-surface rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] mb-3">On This Page</div>
                <ul className="space-y-2 text-sm">
                  <Toc href="#educational">Educational Purpose Only</Toc>
                  <Toc href="#no-investment">No Investment Management</Toc>
                  <Toc href="#risk">Risk Warning</Toc>
                  <Toc href="#jurisdiction">Jurisdiction</Toc>
                  <Toc href="#acknowledge">Your Acknowledgement</Toc>
                </ul>
                <div className="mt-5 pt-5 border-t border-border">
                  <Link to="/" data-testid="disclaimer-aside-home">
                    <Button variant="ghost" className="w-full gap-2 text-white border border-[rgba(245,185,66,0.35)] hover:bg-[rgba(245,185,66,0.08)]">
                      <ArrowLeft className="h-4 w-4" /> Back to Home
                    </Button>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer settings={null} />
    </main>
  );
}

function Block({ icon: Icon, title, children }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  // Map specific ids for ToC matching
  const aliasMap = {
    'educational-purpose-only': 'educational',
    'no-investment-management': 'no-investment',
    'risk-warning': 'risk',
    'jurisdiction': 'jurisdiction',
    'your-acknowledgement': 'acknowledge',
  };
  const anchor = aliasMap[id] || id;
  return (
    <section id={anchor} className="rounded-2xl card-surface p-5 sm:p-7 scroll-mt-20">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[rgba(245,185,66,0.08)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.28)]">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <h2 className="font-display uppercase text-2xl sm:text-3xl tracking-wide">{title}</h2>
      </div>
      <div className="text-[15px] leading-relaxed text-[hsl(0_0%_88%)] space-y-3">
        {children}
      </div>
    </section>
  );
}

function Em({ children, className = '' }) {
  return <strong className={`text-white font-semibold ${className}`}>{children}</strong>;
}

function Li({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[hsl(43_92%_56%)] shrink-0" />
      <span className="text-[hsl(0_0%_88%)]">{children}</span>
    </li>
  );
}

function Toc({ href, children }) {
  return (
    <li>
      <a href={href} className="text-muted-foreground hover:text-[hsl(43_92%_56%)] transition-colors">{children}</a>
    </li>
  );
}
